import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { Container } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import TrepHeader from './TrepHeader';
import { InsertTemplate } from '@styled-icons/icomoon';
import { Workflow } from '@styled-icons/octicons';
import { Security } from '@styled-icons/material-rounded';
import { CloudUpload } from '@styled-icons/material';
import './CommunityProgram.css';

const FAQ_ITEMS = [
  {
    question: 'Is the Community Program free?',
    answer:
      'Yes. Accepted organizations receive full access to the platform at no cost for 6 months, including 250 AI credits per month. There are no hidden fees or payment requirements.',
  },
  {
    question: 'Can we renew after 6 months?',
    answer:
      'Renewal is evaluated on a case-by-case basis. Organizations that actively use the platform and provide feedback are prioritized. You will be contacted before your access expires to discuss options.',
  },
  {
    question: 'Is it safe for sensitive data?',
    answer:
      'Yes. Community Program workspaces have the same security infrastructure as paid tiers, including encryption at rest and in transit, role-based access control, and full audit trail on every action.',
  },
  {
    question: "What's included vs. paid plans?",
    answer:
      'You get structured intake templates, standard review workflow, full audit trail, and publishable artifacts. The program excludes SSO, advanced RBAC, custom workflow design, and dedicated SLA — those are available on Growth and Enterprise plans.',
  },
  {
    question: 'What are the support expectations?',
    answer:
      'Community Program participants receive email support with best-effort response times. There is no guaranteed SLA. We also ask participants to share feedback periodically to help improve the platform.',
  },
];

const INITIAL_FORM = {
  orgName: '',
  website: '',
  orgType: '',
  einId: '',
  contactName: '',
  email: '',
  useCase: '',
  useCaseDetail: '',
  expectedUsers: '',
  reportingFrequency: '',
  feedbackConsent: false,
};

function CommunityProgram() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.orgName.trim()) newErrors.orgName = 'Required';
    if (!form.orgType) newErrors.orgType = 'Required';
    if (!form.contactName.trim()) newErrors.contactName = 'Required';
    if (!form.email.trim()) newErrors.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = 'Invalid email';
    if (!form.useCase) newErrors.useCase = 'Required';
    if (!form.useCaseDetail.trim()) newErrors.useCaseDetail = 'Required';
    if (!form.expectedUsers) newErrors.expectedUsers = 'Required';
    if (!form.reportingFrequency) newErrors.reportingFrequency = 'Required';
    if (!form.feedbackConsent)
      newErrors.feedbackConsent = 'You must agree to provide feedback';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSending(true);
    emailjs.send('service_uo3jp9u', 'template_3vfyyol', {
      org_name: form.orgName,
      website: form.website || 'N/A',
      org_type: form.orgType,
      ein_id: form.einId || 'N/A',
      contact_name: form.contactName,
      email: form.email,
      use_case: form.useCase,
      use_case_detail: form.useCaseDetail,
      expected_users: form.expectedUsers,
      reporting_frequency: form.reportingFrequency,
      feedback_consent: form.feedbackConsent ? 'Yes' : 'No',
    }, '6L3RLPZ8rg0yWc0rw')
    .then(() => {
      setSubmitted(true);
    })
    .catch(() => {
      setErrors({ form: 'Something went wrong. Please try again.' });
    })
    .finally(() => {
      setSending(false);
    });
  };

  return (
    <div className="community-page">
      <Helmet>
        <title>Community Program | teamreports.io</title>
        <meta
          name="description"
          content="Free structured reporting and governance workflows for mission-driven organizations. Apply for the teamreports Community Program."
        />
      </Helmet>

      <TrepHeader />

      <main className="main-content">
        {/* Hero */}
        <section className="community-hero">
          <Container>
            <h1>Community Program</h1>
            <p className="hero-subhead">
              For mission-driven organizations who need structured intake and
              governed workflows. Limited capacity.
            </p>
            <div className="hero-cta">
              <a href="#apply" className="cta-button primary">
                Apply for Access
              </a>
              <a href="#eligibility" className="cta-button secondary">
                See Eligibility
              </a>
            </div>
          </Container>
        </section>

        {/* Benefits */}
        <section className="benefits-section">
          <Container>
            <h2 className="community-section-title">What You Get</h2>
            <p className="community-section-subtitle">
              Enterprise-grade capabilities for qualifying organizations
            </p>
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon intake">
                  <InsertTemplate />
                </div>
                <h3>Structured Intake Templates</h3>
                <p>
                  Standardized templates to collect stakeholder input in a
                  consistent, governance-ready format
                </p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon workflow">
                  <Workflow />
                </div>
                <h3>Standard Review Workflow</h3>
                <p>
                  Built-in Contribute &rarr; Review &rarr; Publish pipeline with
                  role-based routing
                </p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon audit">
                  <Security />
                </div>
                <h3>Full Audit Trail</h3>
                <p>
                  Every contribution, review, and approval tracked with
                  timestamps and accountability
                </p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon publish">
                  <CloudUpload />
                </div>
                <h3>Publishable Artifacts</h3>
                <p>
                  Export-ready reports and governance documents formatted for
                  stakeholder distribution
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* Eligibility */}
        <section className="eligibility-section" id="eligibility">
          <Container>
            <h2 className="community-section-title">Eligibility</h2>
            <p className="community-section-subtitle">
              Who qualifies for the Community Program
            </p>
            <div className="eligibility-content">
              <ul className="eligibility-list">
                <li>
                  <span className="eligibility-check">&#10003;</span>
                  Verified nonprofit or mission-driven organization
                </li>
                <li>
                  <span className="eligibility-check">&#10003;</span>
                  Recurring reporting or governance use case (not one-time
                  projects)
                </li>
                <li>
                  <span className="eligibility-check">&#10003;</span>
                  Willing to provide periodic feedback to help improve the
                  platform
                </li>
              </ul>
            </div>
          </Container>
        </section>

        {/* Program Limits */}
        <section className="limits-section">
          <Container>
            <h2 className="community-section-title">Program Limits</h2>
            <p className="community-section-subtitle">
              Transparent boundaries so you know exactly what to expect
            </p>
            <div className="limits-panel">
              <div className="limits-grid">
                <div className="limit-item">
                  <span className="limit-icon">&#128197;</span> 6-month access
                </div>
                <div className="limit-item">
                  <span className="limit-icon">&#128101;</span> Up to 10 users
                </div>
                <div className="limit-item">
                  <span className="limit-icon">&#9889;</span> 250 AI
                  credits/month (workspace pooled)
                </div>
                <div className="limit-item">
                  <span className="limit-icon">&#128190;</span> 6-month data
                  retention
                </div>
                <div className="limit-item">
                  <span className="limit-icon">&#128231;</span> Community/email
                  support (no SLA)
                </div>
                <div className="limit-item excluded">
                  <span className="limit-icon">&#10060;</span> Excludes SSO,
                  advanced RBAC, custom workflows
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Application Form */}
        <section className="application-section" id="apply">
          <Container>
            <h2 className="community-section-title">Apply for Access</h2>
            <p className="community-section-subtitle">
              Tell us about your organization and how you plan to use
              teamreports
            </p>
            <div className="application-form-wrapper">
              {submitted ? (
                <div className="success-message">
                  <div className="success-icon">&#10003;</div>
                  <h3>Application Submitted</h3>
                  <p>
                    Thank you for applying to the Community Program. We will
                    review your application and respond within 5 business days at{' '}
                    <strong>{form.email}</strong>.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Organization Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="orgName"
                        value={form.orgName}
                        onChange={handleChange}
                        className={errors.orgName ? 'error' : ''}
                      />
                      {errors.orgName && (
                        <span className="error-message">{errors.orgName}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Website</label>
                      <input
                        type="url"
                        name="website"
                        value={form.website}
                        onChange={handleChange}
                        placeholder="https://"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Organization Type <span className="required">*</span>
                      </label>
                      <select
                        name="orgType"
                        value={form.orgType}
                        onChange={handleChange}
                        className={errors.orgType ? 'error' : ''}
                      >
                        <option value="">Select type...</option>
                        <option value="nonprofit">Nonprofit (501c3)</option>
                        <option value="ngo">NGO</option>
                        <option value="education">
                          Educational Institution
                        </option>
                        <option value="government">Government Agency</option>
                        <option value="social_enterprise">
                          Social Enterprise
                        </option>
                        <option value="other">Other Mission-Driven</option>
                      </select>
                      {errors.orgType && (
                        <span className="error-message">{errors.orgType}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>EIN / Registration ID</label>
                      <input
                        type="text"
                        name="einId"
                        value={form.einId}
                        onChange={handleChange}
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Primary Contact Name{' '}
                        <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="contactName"
                        value={form.contactName}
                        onChange={handleChange}
                        className={errors.contactName ? 'error' : ''}
                      />
                      {errors.contactName && (
                        <span className="error-message">
                          {errors.contactName}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>
                        Email <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && (
                        <span className="error-message">{errors.email}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Use Case <span className="required">*</span>
                      </label>
                      <select
                        name="useCase"
                        value={form.useCase}
                        onChange={handleChange}
                        className={errors.useCase ? 'error' : ''}
                      >
                        <option value="">Select use case...</option>
                        <option value="board_reporting">Board Reporting</option>
                        <option value="grant_reporting">Grant Reporting</option>
                        <option value="compliance">
                          Compliance / Regulatory
                        </option>
                        <option value="program_reviews">Program Reviews</option>
                        <option value="stakeholder_updates">
                          Stakeholder Updates
                        </option>
                        <option value="other">Other</option>
                      </select>
                      {errors.useCase && (
                        <span className="error-message">{errors.useCase}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>
                        Expected Users <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        name="expectedUsers"
                        value={form.expectedUsers}
                        onChange={handleChange}
                        min="1"
                        max="10"
                        placeholder="1-10"
                        className={errors.expectedUsers ? 'error' : ''}
                      />
                      {errors.expectedUsers && (
                        <span className="error-message">
                          {errors.expectedUsers}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      Describe Your Use Case{' '}
                      <span className="required">*</span>
                    </label>
                    <textarea
                      name="useCaseDetail"
                      value={form.useCaseDetail}
                      onChange={handleChange}
                      placeholder="Tell us about your reporting or governance needs..."
                      className={errors.useCaseDetail ? 'error' : ''}
                    />
                    {errors.useCaseDetail && (
                      <span className="error-message">
                        {errors.useCaseDetail}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      Reporting Frequency <span className="required">*</span>
                    </label>
                    <select
                      name="reportingFrequency"
                      value={form.reportingFrequency}
                      onChange={handleChange}
                      className={errors.reportingFrequency ? 'error' : ''}
                    >
                      <option value="">Select frequency...</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                    </select>
                    {errors.reportingFrequency && (
                      <span className="error-message">
                        {errors.reportingFrequency}
                      </span>
                    )}
                  </div>

                  <div className="consent-group">
                    <input
                      type="checkbox"
                      name="feedbackConsent"
                      id="feedbackConsent"
                      checked={form.feedbackConsent}
                      onChange={handleChange}
                    />
                    <label htmlFor="feedbackConsent">
                      I agree to provide periodic feedback on the platform to
                      help improve the product for mission-driven organizations.{' '}
                      <span className="required">*</span>
                    </label>
                  </div>
                  {errors.feedbackConsent && (
                    <span className="error-message">
                      {errors.feedbackConsent}
                    </span>
                  )}

                  {errors.form && <div className="form-error">{errors.form}</div>}
                  <button type="submit" className="submit-button" disabled={sending}>
                    {sending ? 'Sending...' : 'Submit Application'}
                  </button>
                </form>
              )}
            </div>
          </Container>
        </section>

        {/* What Happens Next */}
        <section className="next-steps-section">
          <Container>
            <h2 className="community-section-title">What Happens Next</h2>
            <div className="next-steps-grid">
              <div className="next-step-card">
                <div className="step-number">1</div>
                <h3>Application Review</h3>
                <p>
                  Our team reviews your submission within 5 business days to
                  verify eligibility and use case fit.
                </p>
              </div>
              <div className="next-step-card">
                <div className="step-number">2</div>
                <h3>Workspace Setup</h3>
                <p>
                  Accepted organizations receive a dedicated workspace with
                  pre-configured templates and workflows.
                </p>
              </div>
              <div className="next-step-card">
                <div className="step-number">3</div>
                <h3>Onboarding &amp; Access</h3>
                <p>
                  Your team gets invite links, a quick-start guide, and email
                  support to get productive immediately.
                </p>
              </div>
            </div>
          </Container>
        </section>

        {/* FAQ */}
        <section className="faq-section">
          <Container>
            <h2 className="community-section-title">
              Frequently Asked Questions
            </h2>
            <div className="faq-list">
              {FAQ_ITEMS.map((item, idx) => (
                <div className="faq-item" key={idx}>
                  <button
                    className="faq-question"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  >
                    {item.question}
                    <span
                      className={`faq-chevron ${openFaq === idx ? 'open' : ''}`}
                    >
                      &#9660;
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="faq-answer">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </Container>
        </section>
      </main>

      <footer className="footer">
        <Container>
          <div className="footer-content">
            <div className="footer-brand">
              <span className="brand-team">team</span>
              <span className="brand-reports">reports</span>
              <span className="brand-team">.i</span>
              <span className="brand-reports">o</span>
            </div>
            <div className="footer-links">
              <a href="/#features">Features</a>
              <a href="/#usecases">Use Cases</a>
              <a href="/#pricing">Pricing</a>
              <span className="footer-link-disabled" title="Coming Soon">Docs</span>
            </div>
            <div className="footer-contact">
              &copy; 2026 SBABS Corp. All rights reserved. |
              reachteamreports@gmail.com
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default CommunityProgram;
